/**
 * Sample-accurate metronome using the classic Web Audio "lookahead"
 * pattern (Chris Wilson, "A Tale of Two Clocks"):
 *   - a setInterval-driven scheduler runs every 25ms
 *   - it pre-schedules click notes for the next 100ms via AudioContext
 *   - this keeps timing stable even when the JS event loop is jittery
 *
 * Click sound: short oscillator pop, accent on beat 1 of every 4.
 *
 * Migration note: this module was deliberately NOT rewritten when the rest
 * of the engine moved to Tone.js. The scheduling already runs off the same
 * AudioContext clock that Tone uses, and Tone.Loop is locked to
 * Tone.Transport — which we want kept independent of metronome activity
 * during recording (count-in clicks must fire without Transport running).
 * Touching this would trade simplicity for complexity.
 */
export class Metronome {
  private bpm = 100;
  private running = false;
  private nextNoteTime = 0;
  private currentBeat = 0;
  private lookaheadHandle: ReturnType<typeof setInterval> | null = null;

  private readonly LOOKAHEAD_MS = 25;
  private readonly SCHEDULE_AHEAD_S = 0.1;

  constructor(private readonly ctx: AudioContext) {}

  setBpm(bpm: number): void {
    this.bpm = Math.max(20, Math.min(300, Math.round(bpm)));
  }
  getBpm(): number {
    return this.bpm;
  }

  isRunning(): boolean {
    return this.running;
  }

  start(): void {
    this.startAt(this.ctx.currentTime + 0.05);
  }

  /** Begin the continuous click loop with the first beat at `when` in
   *  AudioContext seconds. Used by recording-time metronome to align the
   *  first downbeat with the recorder start (post count-in). */
  startAt(when: number): void {
    if (this.running) this.stop();
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    this.running = true;
    this.currentBeat = 0;
    this.nextNoteTime = when;
    this.lookaheadHandle = setInterval(() => this.scheduler(), this.LOOKAHEAD_MS);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.lookaheadHandle !== null) {
      clearInterval(this.lookaheadHandle);
      this.lookaheadHandle = null;
    }
  }

  toggle(): void {
    if (this.running) this.stop();
    else this.start();
  }

  private scheduler(): void {
    const horizon = this.ctx.currentTime + this.SCHEDULE_AHEAD_S;
    while (this.nextNoteTime < horizon) {
      this.scheduleClick(this.nextNoteTime, this.currentBeat % 4 === 0);
      this.nextNoteTime += 60 / this.bpm;
      this.currentBeat++;
    }
  }

  /** Schedule a single click sound at audio-context time `when`. Public so
   * the recorder can use it for count-in beats. */
  scheduleClickAt(when: number, accent: boolean): void {
    this.scheduleClick(when, accent);
  }

  private scheduleClick(when: number, accent: boolean): void {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.value = accent ? 1500 : 900;
    osc.type = 'square';
    // Tiny envelope: -60dB → -8dB → -60dB over ~50ms
    gain.gain.setValueAtTime(0.001, when);
    gain.gain.exponentialRampToValueAtTime(accent ? 0.45 : 0.3, when + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.001, when + 0.05);
    osc.connect(gain).connect(this.ctx.destination);
    osc.start(when);
    osc.stop(when + 0.06);
  }
}

/**
 * Tap-tempo helper. Each call to `tap()` records a tap and returns the
 * inferred BPM (or null if not enough taps yet).
 *
 * Median-of-intervals for robustness against the occasional jittery tap.
 * Taps reset if there's a >2s gap (user starting over).
 */
export class TapTempo {
  private taps: number[] = [];
  private readonly MAX_TAPS = 8;
  private readonly STALE_MS = 2000;

  tap(): number | null {
    const now = performance.now();
    if (this.taps.length > 0 && now - this.taps[this.taps.length - 1] > this.STALE_MS) {
      this.taps = [];
    }
    this.taps.push(now);
    if (this.taps.length > this.MAX_TAPS) this.taps.shift();
    if (this.taps.length < 2) return null;

    const intervals: number[] = [];
    for (let i = 1; i < this.taps.length; i++) {
      intervals.push(this.taps[i] - this.taps[i - 1]);
    }
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)];
    return Math.round(60_000 / median);
  }

  reset(): void {
    this.taps = [];
  }

  get tapCount(): number {
    return this.taps.length;
  }
}
